import {
    addDays,
    addMinutes,
    differenceInMinutes,
    format,
    getDay,
    isAfter,
    isBefore,
    isValid,
    parse,
    startOfDay,
    subDays,
} from 'date-fns';

import {
    Days,
    DaysEnum,
    HoursOfOperation,
    HoursOfOperations,
} from '@barboxx/types';

export type OrderingAvailabilityReason =
    | 'outsideOpenHours'
    | 'salesCutoffPassed';

export interface HoursWindow {
    day: Days;
    hours: HoursOfOperation;
    openAt: Date;
    closeAt: Date;
}

export interface EvaluateOrderingAvailabilityOptions {
    hoursOfOperations?: HoursOfOperations | null;
    lastCall?: string | null;
    salesCutoffBufferMinutes?: number | null;
    now?: Date;
}

export interface ClampSalesCutoffBufferMinutesOptions {
    hoursOfOperations?: HoursOfOperations | null;
    lastCall?: string | null;
    salesCutoffBufferMinutes?: number | null;
}

export interface OrderingAvailability {
    canOrder: boolean;
    reason: OrderingAvailabilityReason | null;
    window: HoursWindow | null;
    lastCallAt: Date | null;
    salesCutoffAt: Date | null;
}

export const DEFAULT_SALES_CUTOFF_BUFFER_MINUTES = 15;

const ORDERED_DAYS: Days[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];

const parseTimeForDate = (time: string, refDate: Date): Date => {
    const timeFormat = time.length === 5 ? 'HH:mm' : 'HH:mm:ss';
    return parse(time, timeFormat, refDate);
};

const resolveSalesCutoffBufferMinutes = (
    salesCutoffBufferMinutes?: number | null
) => {
    if (
        typeof salesCutoffBufferMinutes !== 'number' ||
        Number.isNaN(salesCutoffBufferMinutes)
    ) {
        return DEFAULT_SALES_CUTOFF_BUFFER_MINUTES;
    }

    const normalized = Math.floor(salesCutoffBufferMinutes);
    return normalized < 0 ? 0 : normalized;
};

export function parseHoursOfOperationTime(
    time: string,
    refDate: Date = new Date()
) {
    return parseTimeForDate(time, refDate);
}

export function getHoursWindowForDate(
    data: HoursOfOperations | null | undefined,
    date: Date
): HoursWindow | null {
    const day = DaysEnum[getDay(date)] as Days;
    const hours = data?.[day] ?? null;

    if (!hours?.open || !hours?.close) {
        return null;
    }

    const openAt = parseHoursOfOperationTime(hours.open, date);
    const closeAtCandidate = parseHoursOfOperationTime(hours.close, date);

    if (!isValid(openAt) || !isValid(closeAtCandidate)) {
        return null;
    }

    const closeAt = isAfter(closeAtCandidate, openAt)
        ? closeAtCandidate
        : addDays(closeAtCandidate, 1);

    return {
        day,
        hours,
        openAt,
        closeAt,
    };
}

export function resolveCurrentHoursWindow(
    data: HoursOfOperations | null | undefined,
    now: Date = new Date()
): HoursWindow | null {
    const today = startOfDay(now);
    const yesterday = subDays(today, 1);

    const windows = [
        getHoursWindowForDate(data, today),
        getHoursWindowForDate(data, yesterday),
    ].filter((window): window is HoursWindow => !!window);

    const activeWindows = windows
        .filter(
            window =>
                !isBefore(now, window.openAt) && isBefore(now, window.closeAt)
        )
        .sort((a, b) => b.openAt.getTime() - a.openAt.getTime());

    return activeWindows[0] ?? null;
}

/**
 * Returns the hours of operation currently in effect for the business,
 * accounting for overnight schedules (close < open means close is next day).
 * Returns null if the business is closed at the current time.
 * @author Copilot
 */
export function getTodaysHoursOfOperation(
    data: HoursOfOperations
): HoursOfOperation | null {
    const now = new Date();
    const dayOfWeek = getDay(now);
    const currentDay = DaysEnum[dayOfWeek] as Days;
    const previousDay = DaysEnum[(dayOfWeek - 1 + 7) % 7] as Days;

    const previousHours = data?.[previousDay] as HoursOfOperation | null;

    // Check if we're in the early hours of an overnight schedule from the previous day
    if (previousHours?.close && previousHours?.open) {
        const prevClose = parseHoursOfOperationTime(previousHours.close);
        const prevOpen = parseHoursOfOperationTime(previousHours.open);

        if (isBefore(prevClose, prevOpen) && isBefore(now, prevClose)) {
            return previousHours;
        }
    }

    return data?.[currentDay] ?? null;
}

/**
 * Parses a business last call time string (supports legacy HH:mm and HH:mm:ss).
 * Only rolls forward to the next calendar day when the last call hour is before
 * noon — indicating an overnight schedule (e.g. last call at 01:25 when it is
 * currently 23:00). Evening times that have already passed simply mean last
 * call is over for today and should NOT roll forward.
 * @param lastCall - Raw lastCall string from the business model
 * @param ref - Reference date to parse against (defaults to now)
 * @returns Resolved Date for the last call time
 * @author Copilot
 */
export function parseLastCallTime(
    lastCall: string,
    ref: Date = new Date()
): Date {
    const lastCallTime = parseTimeForDate(lastCall, ref);
    // Only treat as next-day if it's an early-morning time (overnight bar schedule)
    const isOvernightTime = lastCallTime.getHours() < 12;
    if (isOvernightTime && !isBefore(ref, lastCallTime)) {
        return addDays(lastCallTime, 1);
    }
    return lastCallTime;
}

export function evaluateOrderingAvailability({
    hoursOfOperations,
    lastCall,
    salesCutoffBufferMinutes,
    now = new Date(),
}: EvaluateOrderingAvailabilityOptions): OrderingAvailability {
    const window = resolveCurrentHoursWindow(hoursOfOperations, now);

    if (!window) {
        return {
            canOrder: false,
            reason: 'outsideOpenHours',
            window: null,
            lastCallAt: null,
            salesCutoffAt: null,
        };
    }

    if (!lastCall) {
        return {
            canOrder: true,
            reason: null,
            window,
            lastCallAt: null,
            salesCutoffAt: null,
        };
    }

    let lastCallAt = parseHoursOfOperationTime(lastCall, window.openAt);

    if (!isValid(lastCallAt)) {
        return {
            canOrder: true,
            reason: null,
            window,
            lastCallAt: null,
            salesCutoffAt: null,
        };
    }

    if (isBefore(lastCallAt, window.openAt)) {
        lastCallAt = addDays(lastCallAt, 1);
    }

    const cutoffBufferMinutes = resolveSalesCutoffBufferMinutes(
        salesCutoffBufferMinutes
    );

    const computedCutoff = addMinutes(lastCallAt, cutoffBufferMinutes);
    const salesCutoffAt = isAfter(computedCutoff, window.closeAt)
        ? window.closeAt
        : computedCutoff;

    if (!isBefore(now, salesCutoffAt)) {
        return {
            canOrder: false,
            reason: 'salesCutoffPassed',
            window,
            lastCallAt,
            salesCutoffAt,
        };
    }

    return {
        canOrder: true,
        reason: null,
        window,
        lastCallAt,
        salesCutoffAt,
    };
}

export function getMaxSalesCutoffBufferMinutes(
    hoursOfOperations?: HoursOfOperations | null,
    lastCall?: string | null
): number | null {
    if (!hoursOfOperations || !lastCall) {
        return null;
    }

    const referenceSunday = startOfDay(new Date(2026, 0, 4));

    const minuteGaps = ORDERED_DAYS.reduce<number[]>((acc, day, dayIndex) => {
        const dayHours = hoursOfOperations[day];

        if (!dayHours?.open || !dayHours?.close) {
            return acc;
        }

        const referenceDate = addDays(referenceSunday, dayIndex);
        const openAt = parseHoursOfOperationTime(dayHours.open, referenceDate);
        const closeAtCandidate = parseHoursOfOperationTime(
            dayHours.close,
            referenceDate
        );

        if (!isValid(openAt) || !isValid(closeAtCandidate)) {
            return acc;
        }

        const closeAt = isAfter(closeAtCandidate, openAt)
            ? closeAtCandidate
            : addDays(closeAtCandidate, 1);

        let lastCallAt = parseHoursOfOperationTime(lastCall, openAt);

        if (!isValid(lastCallAt)) {
            return acc;
        }

        if (isBefore(lastCallAt, openAt)) {
            lastCallAt = addDays(lastCallAt, 1);
        }

        if (!isBefore(lastCallAt, closeAt)) {
            return acc;
        }

        const gapMinutes = Math.max(
            0,
            differenceInMinutes(closeAt, lastCallAt)
        );

        acc.push(gapMinutes);
        return acc;
    }, []);

    if (!minuteGaps.length) {
        return null;
    }

    return Math.min(...minuteGaps);
}

export function clampSalesCutoffBufferMinutes({
    hoursOfOperations,
    lastCall,
    salesCutoffBufferMinutes,
}: ClampSalesCutoffBufferMinutesOptions) {
    const normalizedBuffer = resolveSalesCutoffBufferMinutes(
        salesCutoffBufferMinutes
    );

    const maxBuffer = getMaxSalesCutoffBufferMinutes(
        hoursOfOperations,
        lastCall
    );

    if (typeof maxBuffer !== 'number') {
        return {
            value: normalizedBuffer,
            max: null,
            wasClamped: false,
        };
    }

    const clampedValue = Math.min(normalizedBuffer, maxBuffer);

    return {
        value: clampedValue,
        max: maxBuffer,
        wasClamped: clampedValue !== normalizedBuffer,
    };
}

export function getOrderingUnavailableMessage(
    orderingAvailability: OrderingAvailability
): string {
    if (orderingAvailability.reason === 'salesCutoffPassed') {
        if (
            orderingAvailability.salesCutoffAt &&
            orderingAvailability.lastCallAt
        ) {
            return `Sales cutoff passed at ${format(orderingAvailability.salesCutoffAt, 'h:mm a')} (last call ${format(orderingAvailability.lastCallAt, 'h:mm a')}). No new orders can be placed.`;
        }

        return 'Sales cutoff has passed. No new orders can be placed.';
    }

    return 'This business is currently outside serving hours.';
}
